<template>
    <div>
      <!-- Page d'accueil -->
      <div v-if="homePage" class="home-container">
        <component 
          :is="dynamicComponent" 
          :homeImage="homeImage" 
          @exit="exitHomePage"
        />
      </div>
  
      <!-- Galerie 3D -->
      <div v-else class="gallery" id="container" ref="galleryContainer">
        <div class="element" v-for="(resource, index) in resources" :key="index">
          <div class="frame">
            <div class="mat">
              <div class="art">
                <!-- Affichage selon le type de ressource -->
                <template v-if="isImage(resource.url)">
                  <img class="img" :src="resource.url" />
                </template>
                <template v-else-if="isVideo(resource.url)">
                  <video class="video" controls>
                    <source :src="resource.url" type="video/mp4">
                    Votre navigateur ne supporte pas la vidéo.
                  </video>
                </template>
                <template v-else-if="isAudio(resource.url)">
                  <audio class="audio" controls>
                    <source :src="resource.url" type="audio/mpeg">
                    Votre navigateur ne supporte pas l'audio.
                  </audio>
                </template>
                <template v-else-if="isPDF(resource.url)">
                  <embed class="pdf" :src="resource.url" type="application/pdf" width="500" height="600" />
                </template>
              </div>
            </div>
          </div>
          <!-- Description de l'œuvre avec modal -->
          <div v-if="resource.description" class="description" @click="showCartel(index)">
            <p :id="'description' + index" class="description-text">{{ resource.description }}</p>
          </div>
          <div :class="'modalClose modal' + index">
            <h5>Description</h5>
            <p>{{ resource.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onBeforeMount, onMounted, onUnmounted, computed, defineProps } from 'vue';
  import axios from 'axios';
  import { useRoute } from 'vue-router';
  import Modele_2 from "../../components/Models/Modele_2.vue";
  import Modele_3 from "../../components/Models/Modele_3.vue";
  import Modele_1 from "../../components/Models/Modele_1.vue";
  import Modele_4 from "../../components/Models/Modele_4.vue";
  import Modele_5 from "../../components/Models/Modele_5.vue";
  import Modele_6 from "../../components/Models/Modele_6.vue";
  import Modele_7 from "../../components/Models/Modele_7.vue";
  

  const props = defineProps([
    'homePageProps',
    'exhibitId'
  ]);

  // Initialisation des références
  const route = useRoute();
  const id = ref(route.params.id ?? props.exhibitId);

  const resources = ref([]);
  const name = ref('');
  const userName = ref('');
  const homeImage = ref({});
  const homePage = ref(props.homePageProps ?? true );
  const selectedTemplate = ref('');
  const resourcesName = ref([]);
  const galleryContainer = ref(null);

  // Définition des composants dynamiques
  const modeleComponents = {
    Modele_2,
    Modele_3,
    Modele_4,
    Modele_5,
    Modele_6,
    Modele_7,
    Modele_1
  };
  
  const dynamicComponent = computed(() => {
    return modeleComponents[selectedTemplate.value] || null;
  });
  
  // Fonctions utilitaires pour détecter le type de fichier
  function extractFileNameFromUrl(url) {
    return url.split('?')[0].split('/').pop(); 
  }
  
  const isImage = (url) => /\.(jpg|jpeg|png|gif|webp)$/i.test(extractFileNameFromUrl(url));
  const isVideo = (url) => /\.(mp4|mov|avi)$/i.test(extractFileNameFromUrl(url));
  const isAudio = (url) => /\.(mp3|wav)$/i.test(extractFileNameFromUrl(url));
  const isPDF = (url) => /\.pdf$/i.test(extractFileNameFromUrl(url));
  
  // Récupération des données depuis l'API
  const getHomeImage = async () => {
    console.log("getHomeImage", id.value)
    try {
      const response = await axios.get(`${process.env.VUE_APP_BASEURL}/exhibit_home/${id.value}`);
      homeImage.value = response.data[0];
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'image d\'accueil:', error);
    }
  };
  
  const getResources = async () => {
    try {
      const resourcesUrl = `${process.env.VUE_APP_BASEURL}/resources`;
      const resourcesResponse = await axios.post(resourcesUrl, { exhibitId: id.value });
      const galleryUrl = `${process.env.VUE_APP_BASEURL}/galleryResources/${id.value}`;
      const galleryResponse = await axios.get(galleryUrl);
      const galleryResourceIds = galleryResponse.data.map(res => res.resource_id);
      const filteredResources = resourcesResponse.data.filter(resource => galleryResourceIds.includes(resource.id));
      resourcesName.value = filteredResources;
  
      // Charger les URLs de ressources
      resources.value = await Promise.all(filteredResources.map(async (element) => {
        const resourceResponse = await axios.get(`${process.env.VUE_APP_BASEURL}/s3Resource/${element.name}`);
        return { url: resourceResponse.data, description: element.description, title: element.title };
      }));
    } catch (error) {
      console.error("Erreur lors de la récupération des ressources:", error);
    }
  };
  
  const getExhibitName = async () => {
    try {
      const response = await axios.get(`${process.env.VUE_APP_BASEURL}/exhibit/${id.value}`);
      name.value = response.data.exhibit_name;
    } catch (error) {
      console.error('Erreur lors de la récupération du nom de l\'exposition:', error);
    }
  };
  
  const getUserName = async () => {
    try {
      const response = await axios.get(`${process.env.VUE_APP_BASEURL}/user/${id.value}`);
      userName.value = response.data.organisation;
    } catch (error) {
      console.error('Erreur lors de la récupération du nom d\'utilisateur:', error);
    }
  };
  
  const getModel = async () => {
    try {
      const response = await axios.get(`${process.env.VUE_APP_BASEURL}/model/${id.value}`);
      selectedTemplate.value = response.data[0].model.replace(/è/g, 'e').replace(/\s+/g, '_');
    } catch (error) {
      console.error('Erreur lors de la récupération du modèle:', error);
    }
  };
  
  // Gérer l'affichage des descriptions en modal
  const showCartel = (index) => {
    const modal = document.querySelector(`.modal${index}`);
    modal.classList.toggle("modalOpen");
    modal.classList.toggle("modalClose");
  };
  
  const exitHomePage = () => {
    homePage.value = false;
  };

  // Fonction pour convertir scroll vertical en horizontal
  const handleWheel = (e) => {
    if (galleryContainer.value) {
      e.preventDefault();
      // Convertir deltaY (vertical) en scrollLeft (horizontal)
      galleryContainer.value.scrollLeft += e.deltaY;
    }
  };

  // Récupération des données de l'exposition au montage
  onBeforeMount(async () => {
    id.value = props.exhibitId ?? route.params.id;
    await getHomeImage();
    await getResources();
    await getExhibitName();
    await getUserName();
    await getModel();
  });

  // Ajouter l'event listener pour la molette
  onMounted(() => {
    if (galleryContainer.value) {
      galleryContainer.value.addEventListener('wheel', handleWheel, { passive: false });
    }
  });

  // Nettoyer à la destruction
  onUnmounted(() => {
    if (galleryContainer.value) {
      galleryContainer.value.removeEventListener('wheel', handleWheel);
    }
  });
  </script>
  
  <style scoped>
  .gallery {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    height: 100vh;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
  }
  
  .element {
    margin: 20px;
    display: flex;
    justify-content: center;
  }
  
  .frame {
    box-shadow: 0 10px 7px -5px rgba(0, 0, 0, 0.3);
    background: white;
    padding: 10px;
    border: 10px solid black;
  }
  
  .img {
    max-width: 100%;
    height: auto;
    max-height: 75vh;
  }
  
  .description {
    height: 90px;
    width: 300px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    font-size: 1em;
    line-height: 15px;
    box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
    margin-top: 1%;
    padding: 2px;
    cursor: pointer;
  }
  
  .modalClose {
    display: none;
  }
  
  .modalOpen {
    display: block;
    position: absolute;
    background-color: white;
    padding: 20px;
    top: 20%;
    width: 55%;
  }
  </style>
  